/**
 * contact — build a vCard (VCF) for QR-based contact exchange, and render it
 * to a QR-code data URL. Scanning the QR in any camera app imports the contact.
 */
import QRCode from 'qrcode';

export interface ContactCard {
  name: string;
  title?: string;
  company?: string;
  email?: string;
  linkedin?: string;
}

/** RFC 6350 vCard 3.0 string. Kept minimal for reliable QR scanning. */
export function buildVCard(c: ContactCard): string {
  const esc = (s: string) => s.replace(/([,;\\])/g, '\\$1').replace(/\r?\n/g, '\\n');
  const lines: string[] = ['BEGIN:VCARD', 'VERSION:3.0'];
  lines.push(`FN:${esc(c.name)}`);
  // Structured name: Family;Given — best-effort split
  const parts = c.name.trim().split(/\s+/);
  const given = parts.shift() ?? '';
  const family = parts.join(' ');
  lines.push(`N:${esc(family)};${esc(given)};;;`);
  if (c.title) lines.push(`TITLE:${esc(c.title)}`);
  if (c.company) lines.push(`ORG:${esc(c.company)}`);
  if (c.email) lines.push(`EMAIL;TYPE=INTERNET:${esc(c.email)}`);
  if (c.linkedin) {
    const url = c.linkedin.startsWith('http') ? c.linkedin : `https://${c.linkedin}`;
    lines.push(`URL:${esc(url)}`);
  }
  lines.push('END:VCARD');
  return lines.join('\r\n');
}

/** Generate a PNG data URL QR code for the given contact's vCard. */
export async function contactQrDataUrl(c: ContactCard): Promise<string> {
  const vcard = buildVCard(c);
  return QRCode.toDataURL(vcard, {
    errorCorrectionLevel: 'M',
    margin: 1,
    width: 320,
    color: { dark: '#000000', light: '#ffffff' },
  });
}
